import React, { useState, useEffect, useRef } from 'react';
import { api } from '../services/api';

// "Suggest a player" modal — for non-hosts who can see a tee time and want to
// recommend someone to the host. Mirrors AddGuestModal's structure (network
// search autocomplete + free-text name/email fallback), plus an optional
// message to the host.
//
// If the suggester picks a real Greentime account, we send suggestedUserId and
// on acceptance it becomes a real player. Otherwise it's a name (+ optional
// email) and on acceptance it becomes a confirmed guest. Either way the host
// gets a notification and chooses to accept or decline.
export default function SuggestPlayerModal({ teeTime, onClose, onSuggested }) {
  const [name,  setName]  = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState(null);   // selected real-account user obj
  const [hovered, setHovered] = useState(null);
  const debounceRef = useRef(null);

  // Debounced network search as the user types (only while no account picked)
  useEffect(() => {
    if (picked) { setSuggestions([]); return; }
    const q = name.trim();
    if (q.length < 2) { setSuggestions([]); return; }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const results = await api.searchNetwork(q, teeTime.id);
        setSuggestions(results || []);
      } catch {
        setSuggestions([]);
      } finally {
        setSearching(false);
      }
    }, 200);

    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [name, picked, teeTime.id]);

  const pickUser = (u) => {
    setPicked(u);
    setName(u.name);
    setSuggestions([]);
    setError('');
  };

  const clearPicked = () => {
    setPicked(null);
    setName('');
    setError('');
  };

  // First name for the hint copy under the message field — falls back to
  // "them" if nothing's been typed/picked yet.
  const displayName = picked ? picked.name.split(' ')[0] : (name.trim().split(' ')[0] || 'them');

  const submit = async (e) => {
    e.preventDefault();
    setError('');

    if (!picked) {
      if (!name.trim()) { setError('Name is required.'); return; }
      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setError('Please enter a valid email address.');
        return;
      }
    }

    setLoading(true);
    try {
      const payload = picked
        ? { suggestedUserId: picked.id, suggestedName: picked.name, message: message.trim() || undefined }
        : { suggestedName: name.trim(), suggestedEmail: email.trim() || undefined, message: message.trim() || undefined };
      await api.suggestPlayer(teeTime.id, payload);
      if (onSuggested) onSuggested();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <h3>Suggest a player</h3>

        {/* Context banner */}
        <div style={{
          background: '#f4f1eb', borderRadius: 9,
          padding: '0.75rem', marginBottom: '1rem',
        }}>
          <div style={{ fontSize: 14, fontWeight: 500, color: '#0d2b1a' }}>
            {teeTime.course_name}
          </div>
          <div style={{ fontSize: 12, color: '#7a7a6a', marginTop: 2 }}>
            The host will get a notification and can accept or decline.
          </div>
        </div>

        <form onSubmit={submit}>
          {/* Name with smart suggestions */}
          <div className="form-group" style={{ position: 'relative' }}>
            <label className="form-label">
              Player name <span style={{ color: '#c45a3a' }}>*</span>
            </label>

            {picked ? (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: '#e8f5ec', border: '1px solid #7ec99a',
                borderRadius: 8, padding: '8px 10px',
              }}>
                <div style={{
                  width: 28, height: 28, borderRadius: '50%',
                  background: '#7ec99a', color: '#0d2b1a',
                  fontSize: 11, fontWeight: 600,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>{picked.initials}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 500, color: '#0d2b1a' }}>{picked.name}</div>
                  <div style={{ fontSize: 11, color: '#5a5a4a' }}>
                    {picked.is_friend && <span>Friend</span>}
                    {picked.is_friend && picked.mutual_groups?.length > 0 && <span> · </span>}
                    {picked.mutual_groups?.length > 0 && (
                      <span>{picked.mutual_groups.slice(0, 2).join(', ')}{picked.mutual_groups.length > 2 ? ` +${picked.mutual_groups.length - 2}` : ''}</span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={clearPicked}
                  aria-label="Clear selection"
                  style={{
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    fontSize: 18, color: '#5a5a4a', padding: '0 4px',
                  }}
                >×</button>
              </div>
            ) : (
              <input
                className="form-input"
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Start typing a name…"
                autoFocus
                autoComplete="off"
              />
            )}

            {/* Suggestion dropdown */}
            {!picked && suggestions.length > 0 && (
              <div style={{
                marginTop: 4, background: '#fff',
                border: '1px solid #d8d4c4', borderRadius: 8,
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                maxHeight: 240, overflowY: 'auto',
              }}>
                {suggestions.map(u => (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => pickUser(u)}
                    onMouseEnter={() => setHovered(u.id)}
                    onMouseLeave={() => setHovered(null)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      width: '100%', padding: '8px 10px',
                      background: hovered === u.id ? '#f4f1eb' : '#fff',
                      border: 'none', borderBottom: '0.5px solid #f0ede4',
                      cursor: 'pointer', textAlign: 'left',
                    }}
                  >
                    <div style={{
                      width: 28, height: 28, borderRadius: '50%',
                      background: '#d8e6d4', color: '#0d2b1a',
                      fontSize: 11, fontWeight: 600,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>{u.initials}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: '#0d2b1a', fontWeight: 500 }}>{u.name}</div>
                      <div style={{ fontSize: 11, color: '#7a7a6a' }}>
                        {u.is_friend && <span>Friend</span>}
                        {u.is_friend && u.mutual_groups?.length > 0 && <span> · </span>}
                        {u.mutual_groups?.length > 0 && (
                          <span>{u.mutual_groups.slice(0, 2).join(', ')}{u.mutual_groups.length > 2 ? ` +${u.mutual_groups.length - 2}` : ''}</span>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Hint text */}
            {!picked && (
              <div style={{ fontSize: 11, color: '#7a7a6a', marginTop: 4, lineHeight: 1.5 }}>
                {searching
                  ? 'Searching…'
                  : name.trim().length >= 2 && suggestions.length === 0
                    ? "No matches in your network — they'll be suggested as a guest."
                    : 'Type a name to find friends or group members, or suggest anyone as a guest.'}
              </div>
            )}
          </div>

          {/* Email — guest path only */}
          {!picked && (
            <div className="form-group">
              <label className="form-label">
                Email address{' '}
                <span style={{ color: '#9a9a8a', fontWeight: 400,
                               textTransform: 'none', letterSpacing: 0 }}>
                  (optional)
                </span>
              </label>
              <input
                className="form-input"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="dave@email.com"
              />
              <div style={{ fontSize: 11, color: '#7a7a6a', marginTop: 4, lineHeight: 1.5 }}>
                If the host accepts and you've added an email, we'll send them the
                tee time details then.
              </div>
            </div>
          )}

          {/* Optional message to the host */}
          <div className="form-group">
            <label className="form-label">
              Message to the host{' '}
              <span style={{ color: '#9a9a8a', fontWeight: 400,
                             textTransform: 'none', letterSpacing: 0 }}>
                (optional)
              </span>
            </label>
            <textarea
              className="form-input"
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="e.g. Dave's a lock — already cleared it with his wife."
              rows={3}
              style={{ resize: 'vertical', fontFamily: 'inherit' }}
            />
            <div style={{
              background: '#eef0fb', borderRadius: 8,
              padding: '8px 10px', marginTop: 6,
            }}>
              <div style={{ fontSize: 11, color: '#534AB7', lineHeight: 1.5, marginBottom: 3 }}>
                You're putting {displayName}'s name in pencil, not pen. Maybe not even pencil — more like a suggestion written in the sand.
              </div>
              <div style={{ fontSize: 11, color: '#534AB7', lineHeight: 1.5 }}>
                Real talk: has {displayName} committed, or did you just think of them in the shower? Either way, let the host know.
              </div>
            </div>
          </div>

          {error && <p className="form-error">{error}</p>}

          <div className="modal-actions">
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-accent btn-sm"
              disabled={loading}
            >
              {loading ? 'Sending…' : 'Send suggestion'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
